import { useId, useState } from 'react';

/**
 * Campo para elegir de una lista larga escribiendo.
 *
 * POR QUE EXISTE
 *
 * El selector de platillo era un desplegable normal. Con 17 recetas y creciendo
 * cada semana, encontrar una en el telefono es bajar y bajar. Escribir "po" y ver
 * pollo, pozole y pollo en crema es mucho mas rapido.
 *
 * COMO ELIGE
 *
 * Se escribe libre y se resuelve por nombre. Debajo aparecen las opciones que
 * coinciden, como botones, y al tocar una queda elegida.
 *
 * Los botones NO son un adorno del datalist del navegador: son la garantia. El
 * desplegable nativo se comporta distinto en cada telefono y a veces no aparece.
 * Con los botones siempre se puede elegir aunque el navegador no ayude.
 *
 * Mientras el texto no corresponda a UNA opcion exacta, se devuelve null y quien
 * usa el campo sabe que todavia no hay eleccion. Escribir "po" no elige nada:
 * elegir es tocar la opcion o escribir el nombre completo.
 */

export interface OpcionBuscador {
  valor: string;
  etiqueta: string;
  /** Texto secundario, por ejemplo el precio. */
  detalle?: string;
}

interface Props {
  etiqueta: string;
  /** Opcion elegida, o null. */
  valor: string | null;
  opciones: readonly OpcionBuscador[];
  onCambio: (valor: string | null) => void;
  ayuda?: string;
  /** Cuantas coincidencias se muestran como botones. */
  maximo?: number;
}

/** Sin acentos y en minusculas: escribir "limon" debe encontrar "Limón". */
function normalizar(texto: string): string {
  return texto
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function Buscador({
  etiqueta,
  valor,
  opciones,
  onCambio,
  ayuda,
  maximo = 8,
}: Props) {
  const elegida = opciones.find((o) => o.valor === valor);
  const [texto, setTexto] = useState(elegida?.etiqueta ?? '');
  const idCampo = useId();
  const idLista = useId();

  const buscado = normalizar(texto);

  /** Coincidencias por trozo del nombre, no solo por como empieza. */
  const coincidencias =
    buscado === '' ? opciones : opciones.filter((o) => normalizar(o.etiqueta).includes(buscado));

  const exacta = opciones.filter((o) => normalizar(o.etiqueta) === buscado);

  function escribir(nuevo: string) {
    setTexto(nuevo);
    const iguales = opciones.filter((o) => normalizar(o.etiqueta) === normalizar(nuevo));
    // Solo se elige si el nombre corresponde a UNA opcion. Dos recetas con el
    // mismo nombre no se pueden distinguir por texto, asi que no se adivina.
    onCambio(iguales.length === 1 ? (iguales[0]?.valor ?? null) : null);
  }

  function elegir(opcion: OpcionBuscador) {
    setTexto(opcion.etiqueta);
    onCambio(opcion.valor);
  }

  return (
    <div>
      <label htmlFor={idCampo} className="block text-sm font-medium text-texto">
        {etiqueta}
      </label>

      <input
        id={idCampo}
        type="text"
        value={texto}
        onChange={(e) => escribir(e.target.value)}
        list={idLista}
        autoComplete="off"
        placeholder="Escribe para buscar…"
        className="mt-1 min-h-11 w-full rounded-xl border border-borde-fuerte bg-superficie px-3 py-2 text-base text-texto"
      />
      <datalist id={idLista}>
        {opciones.map((o) => (
          <option key={o.valor} value={o.etiqueta} />
        ))}
      </datalist>

      {/* Elegida: se confirma en pantalla para que no quede duda. */}
      {exacta.length === 1 && valor !== null && (
        <p className="mt-1 text-sm font-medium text-exito">✓ {exacta[0]?.etiqueta}</p>
      )}

      {/* Sin eleccion firme: se ofrecen las coincidencias para tocar. */}
      {valor === null && (
        <>
          {coincidencias.length === 0 ? (
            <p className="mt-1 text-sm text-aviso">
              Nada coincide con “{texto.trim()}”. Revisa cómo se escribe.
            </p>
          ) : (
            <>
              <p className="mt-1 text-xs text-texto-tenue">
                {buscado === ''
                  ? `${opciones.length} para elegir`
                  : `${coincidencias.length} ${
                      coincidencias.length === 1 ? 'coincide' : 'coinciden'
                    }, toca la que quieras`}
              </p>
              <ul className="mt-1 space-y-1">
                {coincidencias.slice(0, maximo).map((opcion) => (
                  <li key={opcion.valor}>
                    <button
                      type="button"
                      onClick={() => elegir(opcion)}
                      className="flex min-h-11 w-full items-baseline justify-between gap-3 rounded-xl border border-borde bg-superficie px-3 text-left text-sm text-texto active:bg-superficie-suave"
                    >
                      <span className="min-w-0 break-words">{opcion.etiqueta}</span>
                      {opcion.detalle !== undefined && (
                        <span className="shrink-0 text-xs tabular-nums text-texto-tenue">
                          {opcion.detalle}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
              {coincidencias.length > maximo && (
                <p className="mt-1 text-xs text-texto-tenue">
                  y {coincidencias.length - maximo} más. Escribe para acotar.
                </p>
              )}
            </>
          )}
        </>
      )}

      {ayuda !== undefined && <p className="mt-1 text-xs text-texto-suave">{ayuda}</p>}
    </div>
  );
}
